import { globalSocketService } from "./globalSocketService";
import { toast } from "react-toastify";
import moment from "moment";
import "moment/locale/es";
import React from "react";
import i18next from "i18next";
import {
	RxBell,
	RxCheckCircled,
	RxCrossCircled,
	RxExclamationTriangle,
	RxCalendar
} from "react-icons/rx";
import { RiGroupLine } from "react-icons/ri";
import { FaRegComment } from "react-icons/fa";

export const notificationsSocketService = {
	registerCallback,
	unregisterCallback,
	onNotificationReceived, // Función pública para recibir notificaciones desde App.js
	onNotificationReadChanged, // Función pública para notificar cuando se marca como leída/no leída
	registerReadChangedCallback,
	unregisterReadChangedCallback,
};

var notificationCallbacks = []; // Array de callbacks registrados para nuevas notificaciones
var readChangedCallbacks = []; // Array de callbacks registrados para cambios de estado de lectura

/**
 * Función pública para recibir notificaciones desde el WebSocket global
 * Se llama desde App.js cuando se recibe una notificación por el topic
 */
function onNotificationReceived(notification) {
	const notificationId = notification.id || `temp-${Date.now()}-${Math.random()}`;
	console.log(`=== NOTIFICACIÓN RECIBIDA [ID: ${notificationId}] ===`);
	console.log("Notificación:", notification);
	console.log(`Timestamp: ${new Date().toISOString()}`);

	// Ejecutar todos los callbacks registrados de forma síncrona para mantener el orden
	notificationCallbacks.forEach((callback, index) => {
		try {
			console.log(`Ejecutando callback ${index + 1}/${notificationCallbacks.length} para notificación ${notificationId}`);
			callback(notification);
		} catch (error) {
			console.error(`Error al ejecutar callback ${index + 1} de notificación ${notificationId}:`, error);
		}
	});

	// Mostrar toast para nuevas notificaciones
	// Usar setTimeout con delay mínimo para asegurar que se procese después de los callbacks
	// Esto ayuda a evitar problemas cuando llegan múltiples notificaciones simultáneamente
	setTimeout(() => {
		console.log(`Mostrando toast para notificación ${notificationId}`);
		showNotificationToast(notification);
	}, 0);
}

/**
 * Registrar un callback para recibir notificaciones
 * El WebSocket ya está suscrito al topic desde App.js
 * @param {function} callback - Función que se ejecutará cuando llegue una notificación
 */
function registerCallback(callback) {
	if (callback && typeof callback === 'function') {
		notificationCallbacks.push(callback);
		console.log("Callback de notificaciones registrado. Total callbacks:", notificationCallbacks.length);
	} else {
		console.error("El callback debe ser una función");
	}
}

/**
 * Desregistrar un callback
 */
function unregisterCallback(callback) {
	const index = notificationCallbacks.indexOf(callback);
	if (index > -1) {
		notificationCallbacks.splice(index, 1);
		console.log("Callback de notificaciones desregistrado. Total callbacks:", notificationCallbacks.length);
	}
}

/**
 * Función pública para notificar cuando se marca una notificación como leída/no leída
 * Se llama desde Notifications.js cuando se marca una notificación como leída
 */
function onNotificationReadChanged(notificationId, isRead) {
	console.log(`=== NOTIFICACIÓN MARCADA COMO ${isRead ? 'LEÍDA' : 'NO LEÍDA'} ===`);
	console.log("Notification ID:", notificationId);

	// Ejecutar todos los callbacks registrados
	readChangedCallbacks.forEach(callback => {
		try {
			callback(notificationId, isRead);
		} catch (error) {
			console.error("Error al ejecutar callback de cambio de lectura:", error);
		}
	});
}

/**
 * Registrar un callback para cuando se marca una notificación como leída/no leída
 * @param {function} callback - Función que se ejecutará (notificationId, isRead) => void
 */
function registerReadChangedCallback(callback) {
	if (callback && typeof callback === 'function') {
		readChangedCallbacks.push(callback);
		console.log("Callback de cambio de lectura registrado. Total callbacks:", readChangedCallbacks.length);
	} else {
		console.error("El callback debe ser una función");
	}
}

/**
 * Desregistrar un callback de cambio de lectura
 */
function unregisterReadChangedCallback(callback) {
	const index = readChangedCallbacks.indexOf(callback);
	if (index > -1) {
		readChangedCallbacks.splice(index, 1);
		console.log("Callback de cambio de lectura desregistrado. Total callbacks:", readChangedCallbacks.length);
	}
}

/**
 * Función helper para navegar a una ruta
 */
function navigateToRoute(route) {
	if (route) {
		window.location.href = route;
	}
}

/**
 * Etiqueta de acción del toast según la ruta de navegación
 */
function getNavigationActionLabel(route) {
	const labelsByRoute = {
		"/pending-absences": i18next.t("notifications.toastAction.viewRequests"),
		"/absences/": i18next.t("notifications.toastAction.viewAbsence"),
		"/my-times": i18next.t("notifications.toastAction.viewTimeRegistrations"),
		"/calendar/": i18next.t("notifications.toastAction.viewCalendar"),
	};

	return labelsByRoute[route] || i18next.t("notifications.toastAction.viewMore");
}

/**
 * Variante visual del toast según el tipo de notificación
 */
function getNotificationToastVariant(type, notification = null) {
	if (type === "WorkflowStatusChangeEmailEvent" && notification) {
		const body = notification.body || "";
		const subject = notification.subject || "";
		const isRejected =
			body.toLowerCase().includes("rechaz") ||
			body.toLowerCase().includes("deneg") ||
			subject.toLowerCase().includes("rechaz") ||
			subject.toLowerCase().includes("deneg");

		return isRejected ? "error" : "success";
	}

	const variantByType = {
		PlanAssignmentExchangeRequestEmailEvent: "warning",
		TimeRegistrationNotDoneEmailEvent: "warning",
		TimeRegistrationReminderEmailEvent: "warning",
		PlanAssignmentExchangeAcceptEmailEvent: "success",
		PlanAssignmentExchangeStatusChangeEmailEvent: "success",
	};

	return variantByType[type] || "info";
}

/**
 * Componente del toast de notificación
 */
function NotificationToast({ closeToast, data, navigationRoute }) {
	const variant = getNotificationToastVariant(data.type, data);
	const actionLabel = navigationRoute
		? getNavigationActionLabel(navigationRoute)
		: null;

	const handleActionClick = (e) => {
		e.stopPropagation();
		if (navigationRoute) {
			navigateToRoute(navigationRoute);
		}
	};

	return (
		<div className={`plain-toast plain-toast--${variant}`}>
			<div className="plain-toast__icon">
				{getNotificationIcon(data.type, data)}
			</div>
			<div className="plain-toast__content">
				<div className="plain-toast__title">{data.subject}</div>
				{actionLabel && (
					<button
						type="button"
						className="plain-toast__action"
						onClick={handleActionClick}
					>
						{actionLabel}
					</button>
				)}
			</div>
			<button
				type="button"
				className="plain-toast__close"
				aria-label={i18next.t("common.close")}
				onClick={(e) => {
					e.stopPropagation();
					closeToast();
				}}
			>
				×
			</button>
		</div>
	);
}

/**
 * Obtener la ruta de navegación según el tipo de notificación
 */
function getNavigationRoute(notificationType) {
	const navigationRoutes = {
		WorkflowRequestEmailEvent: "/pending-absences",
		WorkflowRequestSecondLevelEmailEvent: "/pending-absences",
		WorkflowStatusChangeEmailEvent: "/absences/",
		TimeRegistrationReminderEmailEvent: "/my-times",
		TimeRegistrationNotDoneEmailEvent: "/my-times",
		ChatMessageEmailEvent: "/my-times",
		// Las demás notificaciones van a /calendar
		AlertPlanChangedEmailEvent: "/calendar/",
		AlertGroupChangedEmailEvent: "/calendar/",
		PlanAssignmentExchangeRequestEmailEvent: "/calendar/",
		PlanAssignmentExchangeAcceptEmailEvent: "/calendar/",
		PlanAssignmentExchangeStatusChangeEmailEvent: "/calendar/",
	};

	return navigationRoutes[notificationType] || "/calendar/";
}

/**
 * Mostrar un toast para una nueva notificación recibida
 */
function showNotificationToast(notification) {
	const navigationRoute = getNavigationRoute(notification.type);

	const handleToastClick = () => {
		// Navegar cuando se hace click en el toast (excepto en el botón de cerrar)
		if (navigationRoute) {
			navigateToRoute(navigationRoute);
		}
	};

	// Generar un toastId único basado en el ID de la notificación o timestamp
	// Esto asegura que cada toast sea único y no se reemplace cuando llegan múltiples notificaciones
	const toastId = notification.id
		? `notification-${notification.id}`
		: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

	toast(NotificationToast, {
		toastId: toastId, // ID único para cada toast
		data: notification,
		navigationRoute: navigationRoute, // Pasar la ruta al componente
		position: "top-right",
		autoClose: 5000,
		hideProgressBar: true,
		closeOnClick: false,
		onClick: navigationRoute ? handleToastClick : undefined,
		pauseOnHover: true,
		draggable: true,
		theme: "dark",
		closeButton: false,
		className: "notification-toast-container",
	});
}

// Exportar función helper para usar en otros componentes
export const getNotificationNavigationRoute = getNavigationRoute;
export const navigateToNotificationRoute = navigateToRoute;

/**
 * Obtener el icono apropiado según el tipo de notificación
 */
function getNotificationIcon(type, notification = null) {
	// Mapeo de tipos de notificaciones a iconos y colores
	const notificationTypes = {
		// Cambios en planes asignados
		AlertPlanChangedEmailEvent: {
			icon: React.createElement(RxCalendar),
			color: "#8B5CF6", // Púrpura
		},
		// Cambios en grupos
		AlertGroupChangedEmailEvent: {
			icon: React.createElement(RiGroupLine),
			color: "#9CA3AF", // Gris oscuro (como en el mockup)
		},
		// Solicitud de intercambio de turnos
		PlanAssignmentExchangeRequestEmailEvent: {
			icon: React.createElement(RxExclamationTriangle),
			color: "#F59E0B", // Amarillo/Naranja
		},
		// Aceptación de intercambio de turnos
		PlanAssignmentExchangeAcceptEmailEvent: {
			icon: React.createElement(RxCheckCircled),
			color: "#10B981", // Verde
		},
		// Cambio de estado en intercambio de turnos (aprobado/rechazado)
		PlanAssignmentExchangeStatusChangeEmailEvent: {
			// Podría ser aprobado o rechazado, por defecto check
			icon: React.createElement(RxCheckCircled),
			color: "#10B981", // Verde
		},
		// Registro de tiempo no realizado
		TimeRegistrationNotDoneEmailEvent: {
			icon: React.createElement(RxExclamationTriangle),
			color: "#EC4899", // Rosa (como en el mockup)
		},
		// Recordatorio de registro de tiempo
		TimeRegistrationReminderEmailEvent: {
			icon: React.createElement(RxExclamationTriangle),
			color: "#EC4899", // Rosa
		},
		ChatMessageEmailEvent: {
			icon: React.createElement(FaRegComment),
			color: "#5B49FE",
		},
		// Solicitud de workflow/vacaciones (para supervisores)
		WorkflowRequestEmailEvent: {
			icon: React.createElement(RxCalendar),
			color: "#8B5CF6", // Púrpura
		},
		WorkflowRequestSecondLevelEmailEvent: {
			icon: React.createElement(RxCalendar),
			color: "#8B5CF6", // Púrpura
		},
		// Cambio de estado de workflow/vacaciones (para empleados)
		// Puede ser aprobado (CheckCircled) o rechazado (CrossCircled)
		WorkflowStatusChangeEmailEvent: {
			// Por defecto aprobado, pero se puede determinar por el body o subject
			icon: React.createElement(RxCheckCircled),
			color: "#8B5CF6", // Púrpura (como vacaciones aprobadas en mockup)
		},
	};

	// Para WorkflowStatusChangeEmailEvent, intentar determinar si es aprobado o rechazado
	let notificationType = notificationTypes[type];
	if (type === "WorkflowStatusChangeEmailEvent" && notification) {
		const body = notification.body || "";
		const subject = notification.subject || "";
		const isRejected =
			body.toLowerCase().includes("rechaz") ||
			body.toLowerCase().includes("deneg") ||
			subject.toLowerCase().includes("rechaz") ||
			subject.toLowerCase().includes("deneg");

		if (isRejected) {
			notificationType = {
				icon: React.createElement(RxCrossCircled),
				color: "#EF4444", // Rojo para rechazado
			};
		}
	}

	// Si no existe el tipo, usar por defecto
	if (!notificationType) {
		notificationType = {
			icon: React.createElement(RxBell),
			color: "#9CA3AF", // Gris por defecto
		};
	}

	return React.cloneElement(notificationType.icon, {
		style: { color: "#0B0B1E", fontSize: "14px" },
	});
}

/**
 * Formatear fecha como "Hace X minutos/horas/días"
 */
function formatTimeAgo(dateString) {
	moment;
	const date = moment.utc(dateString).local();
	return date.fromNow();
}

